import { useEffect, useState } from "react";
import MasterLayout from "./MasterLayout";
import { masterFetch } from "./masterApi";

export default function MasterSettings() {
  const [data, setData] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setData(await masterFetch("/settings")); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setErr(""); setMsg(""); setBusy(true);
    const body = {};
    if (apiKey.trim()) body.api_key = apiKey.trim();
    if (webhookSecret.trim()) body.webhook_secret = webhookSecret.trim();
    if (Object.keys(body).length === 0) {
      setErr("Skriv inn minst én verdi for å lagre.");
      setBusy(false);
      return;
    }
    try {
      await masterFetch("/settings/stripe", { method: "PUT", body: JSON.stringify(body) });
      setApiKey("");
      setWebhookSecret("");
      setMsg("Lagret. Endringer er aktive umiddelbart.");
      load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function clear(field) {
    if (!window.confirm(`Fjern ${field}?`)) return;
    try {
      const body = field === "api_key" ? { api_key: "__clear__" } : { webhook_secret: "__clear__" };
      await masterFetch("/settings/stripe", { method: "PUT", body: JSON.stringify(body) });
      load();
    } catch (e) { setErr(e.message); }
  }

  const s = data?.stripe || {};

  return (
    <MasterLayout>
      <h1 style={{ margin: "0 0 4px", fontSize: 26, letterSpacing: -0.5 }}>Plattform-innstillinger</h1>
      <div style={{ opacity: 0.55, fontSize: 13, marginBottom: 24 }}>
        Lagres i databasen, ikke i miljøvariabler. Aktiveres umiddelbart.
      </div>

      {err && <Box color="red">{err}</Box>}
      {msg && <Box color="green">{msg}</Box>}

      <div data-testid="stripe-settings" style={card}>
        <h2 style={{ margin: "0 0 4px", fontSize: 18 }}>Stripe</h2>
        <div style={{ opacity: 0.6, fontSize: 13, marginBottom: 18 }}>
          Brukes til tenants&rsquo; SaaS-abonnementer (per-bedrift månedlig fakturering).
        </div>

        <div style={statusRow}>
          <Status label="API-nøkkel" value={s.api_key_set ? s.api_key_masked : "Ikke satt"}
                  ok={!!s.api_key_set} mode={s.mode}
                  onClear={s.api_key_set ? () => clear("api_key") : null} />
          <Status label="Webhook secret" value={s.webhook_secret_set ? "•••••• satt" : "Ikke satt"}
                  ok={!!s.webhook_secret_set}
                  onClear={s.webhook_secret_set ? () => clear("webhook_secret") : null} />
        </div>

        {s.updated_at && (
          <div style={{ marginTop: 14, fontSize: 11, opacity: 0.4 }}>
            Sist oppdatert: {new Date(s.updated_at).toLocaleString()}
          </div>
        )}

        <div style={{ borderTop: "1px solid #2a1c44", marginTop: 22, paddingTop: 22 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 13, letterSpacing: 1.5, opacity: 0.8 }}>OPPDATER NØKLER</h3>

          <Field label="Stripe API-nøkkel (sk_live_... eller sk_test_...)">
            <input data-testid="stripe-api-key" type="password" value={apiKey}
                   onChange={e => setApiKey(e.target.value)} placeholder="sk_live_..." style={inp} />
          </Field>

          <Field label="Webhook signing secret (whsec_...)">
            <input data-testid="stripe-webhook-secret" type="password" value={webhookSecret}
                   onChange={e => setWebhookSecret(e.target.value)} placeholder="whsec_..." style={inp} />
          </Field>

          <button data-testid="save-stripe" onClick={save} disabled={busy} style={btnPrimary}>
            {busy ? "…" : "Lagre"}
          </button>
        </div>
      </div>

      <div style={{ ...card, marginTop: 16 }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 14, opacity: 0.85 }}>Hvor finner jeg nøklene?</h3>
        <ol style={{ paddingLeft: 18, fontSize: 13, opacity: 0.75, lineHeight: 1.8 }}>
          <li>Logg inn på <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" style={lnk}>dashboard.stripe.com</a></li>
          <li><b>Developers &rarr; API keys</b> &rarr; kopier &laquo;Secret key&raquo; (live eller test)</li>
          <li><b>Developers &rarr; Webhooks &rarr; + Add endpoint</b>: URL = <code style={cd}>https://{window.location.host}/api/master/stripe/webhook</code></li>
          <li>Velg events: <code style={cd}>customer.subscription.*</code>, <code style={cd}>invoice.payment_failed</code>, <code style={cd}>invoice.payment_succeeded</code></li>
          <li>Kopier <b>Signing secret</b> som vises og lim inn her over</li>
        </ol>
      </div>
    </MasterLayout>
  );
}

function Box({ color, children }) {
  const palette = color === "red"
    ? { bg: "rgba(255,80,90,0.1)", border: "rgba(255,80,90,0.4)", color: "#ff7886" }
    : { bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.4)", color: "#34d399" };
  return <div style={{ padding: 12, background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color, borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{children}</div>;
}

function Status({ label, value, ok, mode, onClear }) {
  return (
    <div style={statusCard}>
      <div style={{ fontSize: 10, letterSpacing: 2, opacity: 0.5 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 14, fontFamily: "monospace", marginTop: 6, color: ok ? "#34d399" : "#9ca3af" }}>
        {value}
      </div>
      {mode && mode !== "unset" && (
        <span style={{ display: "inline-block", marginTop: 8, padding: "2px 8px", borderRadius: 999, fontSize: 10, letterSpacing: 1.5, fontWeight: 600,
                       background: mode === "live" ? "rgba(239,68,68,0.18)" : "rgba(245,158,11,0.18)",
                       color: mode === "live" ? "#fca5a5" : "#fbbf24",
                       border: `1px solid ${mode === "live" ? "rgba(239,68,68,0.5)" : "rgba(245,158,11,0.5)"}` }}>
          {mode.toUpperCase()}
        </span>
      )}
      {onClear && <button onClick={onClear} style={{ marginLeft: 10, padding: "2px 10px", background: "transparent", color: "#a89ec9", border: "1px solid #3a2a55", borderRadius: 6, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>Fjern</button>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11, letterSpacing: 1.5, opacity: 0.7, marginBottom: 6, textTransform: "uppercase" }}>{label}</label>
      {children}
    </div>
  );
}

const card = { background: "#0f0a1c", border: "1px solid #1f1232", padding: 22, borderRadius: 12 };
const inp = { width: "100%", padding: "10px 12px", background: "#06050d", border: "1px solid #3a2a55", borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "monospace" };
const btnPrimary = { padding: "10px 22px", background: "linear-gradient(135deg,#8b5cf6,#a855f7)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "inherit" };
const statusRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const statusCard = { background: "#06050d", border: "1px solid #2a1c44", padding: 14, borderRadius: 10 };
const cd = { background: "#06050d", padding: "2px 6px", borderRadius: 4, fontSize: 11 };
const lnk = { color: "#a855f7" };
